import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import { paychexService } from "src/app/services/api/paychex.service";
import {
  ProcessedBatch,
  BatchesResponse,
} from "src/app/@types/interfaces/paychex.interface";
import { downloadService } from "src/app/services/api/download.service";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export default function AdminProcessedBatch() {
  const [batches, setBatches] = useState<ProcessedBatch[]>([]);
  const [filtered, setFiltered] = useState<ProcessedBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    program: "Summer Youth Employment Program 2025",
    week: "",
    status: "",
  });

  // load data
  useEffect(() => {
    paychexService.getProcessedBatches().then((resp) => {
      if (resp.success && resp.data) {
        setBatches(resp.data.processedBatches);
        setFiltered(resp.data.processedBatches);

        // Set available filter options from the data
        if (resp.data.filters) {
          setAvailableWeeks(resp.data.filters.weeks || []);
          setAvailableStatuses(resp.data.filters.status || []);
        }
      }
      setLoading(false);
    });
  }, []);

  // apply filters
  useEffect(() => {
    let data = batches;
    if (filters.week) {
      data = data.filter((b) => b.week === filters.week);
    }
    if (filters.status) {
      data = data.filter((b) => b.status === filters.status);
    }
    setFiltered(data);
  }, [filters, batches]);

  const handleFilterChange =
    (field: keyof typeof filters) => (e: SelectChangeEvent<string>) => {
      setFilters((f) => ({ ...f, [field]: e.target.value }));
    };

  const handleDifsChange =
    (batchId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelectedBatchId(batchId);
        setDialogOpen(true);
        e.preventDefault();
      }
    };

  // Handle dialog confirm action
  const handleDialogConfirm = () => {
    if (selectedBatchId) {
      setBatches((all) =>
        all.map((b) =>
          b.batchId === selectedBatchId
            ? { ...b, difsSubmitted: true, status: "Salary Paid" }
            : b
        )
      );
    }
    setDialogOpen(false);
    setSelectedBatchId(null);
  };

  // Handle dialog cancel action
  const handleDialogCancel = () => {
    setDialogOpen(false);
    setSelectedBatchId(null);
  };

  const downloadExcel = (batch: ProcessedBatch) => {
    const filename = "Download_Excel_Admin_Portal.xlsx";
    const filepath = `${process.env.PUBLIC_URL}/assets/data/Download Excel_Admin Portal.xlsx`;
    downloadService.downloadFileWithPath(filename, filepath);
  };

  if (loading) {
    return (
      <>
        <IndexHeader
          name="Processed Batches"
          update={false}
          add={false}
          redirectingPath="/processed-batches"
        />
        <Box m={4} textAlign="center">
          <Typography>Loading…</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <IndexHeader
        name="Processed Batches"
        update={false}
        add={false}
        redirectingPath="/processed-batches"
      />

      <Card variant="outlined" className="m-24 mb-12">
        <Box p={3}>
          <Grid container spacing={4} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Program
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.program}
                  displayEmpty
                  onChange={handleFilterChange("program")}
                  variant="outlined"
                >
                  <MenuItem value="Summer Youth Employment Program 2025">
                    Summer Youth Employment Program 2025
                  </MenuItem>
                  <MenuItem value="In-School Youth Program 2024">
                    In-School Youth Program 2024
                  </MenuItem>
                  <MenuItem value="In-School Youth Program 2023">
                    In-School Youth Program 2023
                  </MenuItem>
                  <MenuItem value="Youth Leadership Program 2025">
                    Youth Leadership Program 2025
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Week
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.week}
                  displayEmpty
                  onChange={handleFilterChange("week")}
                  variant="outlined"
                  renderValue={(selected) => selected || "Select"}
                >
                  <MenuItem value="">All</MenuItem>
                  {availableWeeks.map((week) => (
                    <MenuItem key={week} value={week}>
                      {week}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.status}
                  displayEmpty
                  onChange={handleFilterChange("status")}
                  variant="outlined"
                  renderValue={(selected) => selected || "Select"}
                >
                  <MenuItem value="">All</MenuItem>
                  {availableStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Box className="mx-24">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Program</StyledTableCell>
                <StyledTableCell>Week</StyledTableCell>
                <StyledTableCell>Batch ID</StyledTableCell>
                <StyledTableCell>Batched Date</StyledTableCell>
                <StyledTableCell>Payroll Submitted</StyledTableCell>
                <StyledTableCell>Payroll Processed</StyledTableCell>
                <StyledTableCell>Download</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length > 0 ? (
                filtered.map((row) => (
                  <TableRow key={row.batchId} hover>
                    <StyledTableCell>{row.program}</StyledTableCell>
                    <StyledTableCell>{row.week}</StyledTableCell>
                    <StyledTableCell>{row.batchId}</StyledTableCell>
                    <StyledTableCell>{row.batchedDate}</StyledTableCell>
                    <StyledTableCell>{row.payrollSubmitted}</StyledTableCell>
                    <StyledTableCell>{row.payrollProcessed}</StyledTableCell>

                    <StyledTableCell>
                      <StyledButton
                        variant="contained"
                        size="small"
                        onClick={() => downloadExcel(row)}
                      >
                        Download
                      </StyledButton>
                    </StyledTableCell>

                    <StyledTableCell>{row.status}</StyledTableCell>

                    <StyledTableCell>
                      {row.status === "Salary Processed" ? (
                        <>
                          <Checkbox
                            disabled={row.difsSubmitted}
                            checked={row.difsSubmitted}
                            onChange={handleDifsChange(row.batchId)}
                          />
                          {row.difsSubmitted ? "" : "DIFS Submitted"}
                        </>
                      ) : (
                        <></>
                      )}
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={9} align="center">
                    No records found
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Status Change"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to mark this as Salary Paid? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
